import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, TextField, Button, Stack, Snackbar } from '@mui/material';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useAuth } from '@shared/hooks/useAuth';
import { container } from '@infrastructure/container';
import { useSignUpStore } from '../../../application/store/SignUpStore';
import { completeSignIn } from '../utils/completeSignIn';


const SignInPassword = () => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout } = useAuth();
  const email = decodeURIComponent(new URLSearchParams(location.search).get('email')?.trim() || '');

  const signInOTP = async () => {
    const response = await container.authRepository.sendCode(email);
    if (response.status === 'success' && response.data) {

      useSignUpStore.getState().setUserId(response.data.userId);

      setErrorMessage('Verification code sent to your email');
      navigate(`/sign-in/validate?email=${encodeURIComponent(email)}`);
    }
  }

  const handleSubmit = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      // Only logout if there's an existing session to avoid unnecessary session deletion
      const isLoggedIn = await container.authRepository.isUserLoggedIn();
      if (isLoggedIn.status === 'success' && isLoggedIn.data) {
        await logout();
      }
      
      const result = await container.authRepository.verifyPassword(email, password);

      if (result.status === 'success' && result.data) {
        const user = await container.authRepository.getCurrentUser();
        const jwt = await container.authRepository.createJwt();

        if (user.status === 'error') {
          throw new Error('Failed to get user information');
        }

        if (jwt.status === 'error') {
          throw new Error('Failed to get JWT');
        }

        await completeSignIn({
          navigate,
          user: user.data,
          jwt: jwt.data,
          login,
        });
      } else {
        throw new Error('Invalid password');
      }
    } catch (error) {
      // Only logout if there's an existing session to avoid unnecessary session deletion
      const isLoggedIn = await container.authRepository.isUserLoggedIn();
      if (isLoggedIn.status === 'success' && isLoggedIn.data) {
        logout();
      }
      setErrorMessage(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 0
    }}>
      <DialogContainer      >
        <DialogHeader>
          <img height={16} src="/assets/images/ic_just_stock.png" alt="logo" />
        </DialogHeader>

        <Box sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1
        }}>
          <Box>
            <Typography variant="body1" fontWeight="medium">Password</Typography>
            <Typography variant="body1" color="text.secondary">
              Enter your password to continue
            </Typography>
          </Box>

          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && password) {
                handleSubmit();
              }
            }}
          />

          <Button
            variant="text"
            onClick={signInOTP}
            size="medium"
            sx={{
              width: 'fit-content'
            }}
          >
            Sign in without password
          </Button>

          <Box sx={{ flex: 1 }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{
              justifyContent: 'space-between'
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(`/sign-in?email=${encodeURIComponent(email)}`)}
              size="medium"
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!password || isLoading}
              size="medium"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Stack>
        </Box>
      </DialogContainer>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        message={errorMessage}
      />
    </Box>
  );
};

export default SignInPassword;